import {Button, Flex, Title} from '@priority-software/priority-style-react';
import React, {ChangeEvent, useEffect, useRef} from 'react';
import {useEmailContext} from '../../../../../context';
import {texts} from '../../../../../texts';
import styles from '../../../../styles.module.scss';
import {useEventCallback} from 'usehooks-ts';
import {InsertImagePayload} from '../../../../../../Form/components/HTMLFormEditor/Editor/plugins/ImagesPlugin/ImagesPlugin';
import {INSERT_IMAGE_COMMAND} from '../../../../../../Form/components/HTMLFormEditor/Editor/plugins/ImagesPlugin/commands';
import {readFileAsDataURL} from '../../../../../utils/readerFileAsDataURL';
import {createDataTestId} from '../../../../../../../utils/dataTestId';
import {checkImageSize} from '../../../../../utils/checkHtmlSize';

export const useImageCmp = () => {
    const {
        states: {emailEditor}
    } = useEmailContext();

    const fileInputRef = useRef(null);

    useEffect(() => {
        fileInputRef.current.accept = 'image/*';
    }, []);

    const renderHeader = () => (
        <Title
            componentClasses={styles.flex1}
            skin="h4"
            {...createDataTestId(`email-add-image-header-title`, true)}
        >
            {texts.image_title}
        </Title>
    );

    const InsertImagePayload = useEventCallback((payload: InsertImagePayload) => {
        emailEditor.dispatchCommand(INSERT_IMAGE_COMMAND, {...payload, isDraggable: false});
    });

    const handleButtonClick = useEventCallback(() => {
        fileInputRef.current.click();
    });

    const handleFileChange = useEventCallback(async (event: ChangeEvent<HTMLInputElement>) => {
        const files = event.target.files;

        if (!files || files.length === 0) return;

        try {
            const src = await readFileAsDataURL(files[0]);
            const isBiggerThanMaxSize = checkImageSize(
                src,
                files[0].name?.split?.('.')?.[0] ?? '',
                files[0].size
            );
            if (!isBiggerThanMaxSize) {
                InsertImagePayload({src, altText: files[0].name?.split?.('.')?.[0] ?? ''});
            }
        } catch (error) {
            console.error('Error reading file:', error);
        }
    });

    const renderBody = () => (
        <Flex
            componentClasses={styles.contentContainer}
            direction="column"
            justifyContent="flex-start"
            alignItems="center"
        >
            <Title
                skin="h6"
                componentClasses={styles.textCenter}
                {...createDataTestId(`email-add-image-description`, true)}
            >
                {texts.drag_and_drop}
            </Title>
            <div {...createDataTestId(`email-add-image-button-container`, false)}>
                <input
                    type="file"
                    ref={fileInputRef}
                    style={{display: 'none'}}
                    onChange={handleFileChange}
                    {...createDataTestId(`email-add-image-button-input`, false)}
                />
                <Button
                    skin="secondary"
                    size="medium"
                    onClick={handleButtonClick}
                    {...createDataTestId(`email-add-image-button`, true)}
                >
                    {texts.select_file}
                </Button>
            </div>
        </Flex>
    );

    return {renderHeader, renderBody} as const;
};
